<template>
  <div class="catalog-setting">
    <template v-if="readOnly">
      <base-section-title :title="sectionTitle" class="m-b-24" />
      <el-table :data="form.catalogList" border>
        <el-table-column label="目录序号" type="index" width="80px">
          <template v-slot:default="{ row }">
            <span>{{ row.orderNum }}</span>
          </template>
        </el-table-column>
        <el-table-column label="目录名称">
          <template v-slot:default="{ row }">
            <span>{{ row.catalogName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="是否必须上传">
          <template v-slot:default="{ row }">
            <span>{{ row.uploadRequire ? '是' : '否' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="是否需盖章">
          <template v-slot:default="{ row }">
            <span>{{ row.signRequire ? '是' : '否' }}</span>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template v-else>
      <base-section-title :title="sectionTitle" class="m-b-24" />
      <el-form ref="formRef" :model="form">
        <el-table :data="form.catalogList" border>
          <el-table-column label="目录序号" type="index" width="80px">
            <template v-slot:default="{ row }">
              <el-form-item>
                <span>{{ row.orderNum }}</span>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="目录名称">
            <template v-slot:default="{ row, $index }">
              <el-form-item
                :prop="'catalogList.' + $index + '.catalogName'"
                :rules="rules.catalogName"
              >
                <el-input
                  v-model.trim="row.catalogName"
                  maxlength="50"
                  placeholder="请输入"
                />
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="是否必须上传">
            <template v-slot:default="{ row, $index }">
              <el-form-item
                :prop="'catalogList.' + $index + '.uploadRequire'"
                :rules="rules.uploadRequire"
              >
                <el-select
                  v-model="row.uploadRequire"
                  placeholder="请选择"
                  :disabled="uploadRequireDisabled($index)"
                >
                  <el-option label="是" :value="true" />
                  <el-option label="否" :value="false" />
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="是否需盖章">
            <template v-slot:default="{ row, $index }">
              <el-form-item
                :prop="'catalogList.' + $index + '.signRequire'"
                :rules="rules.signRequire"
              >
                <el-select
                  v-model="row.signRequire"
                  placeholder="请选择"
                  :disabled="signRequireDisabled($index)"
                >
                  <el-option label="是" :value="true" />
                  <el-option label="否" :value="false" />
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="140px">
            <template v-slot:default="{ $index }">
              <el-button
                v-if="$index !== 0"
                type="text"
                @click="upItem($index)"
              >
                上移
              </el-button>
              <el-button
                v-if="showDownItem($index)"
                type="text"
                @click="downItem($index)"
              >
                下移
              </el-button>
              <el-button
                v-if="showRemoveItem($index)"
                type="text"
                @click="removeItem($index)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
      <div class="add-button" @click="addItem">
        <i class="el-icon-plus"></i>
        新增目录
      </div>
    </template>
  </div>
</template>

<script>
import BaseSectionTitle from '@/components/common/BaseSectionTitle.vue'
import { cloneDeep, isEqual } from 'lodash'
import {
  bidFileDirectoryTypeDict,
  bidFileTypeDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import { requiredRule } from '@/utils/form-validate'
import { countRepeatNum, moveInArray } from '@/utils/utils'

/**
 * @typedef FileCatalog
 * @property {number} orderNum 目录序号
 * @property {string} catalogName 目录名称
 * @property {boolean} uploadRequire 是否需要上传
 * @property {boolean} signRequire 是否需要签章
 * @property {number} catalogType 目录类型
 * @property {boolean} showInPage 是否在页面上显示
 * @property {number} bidFileType 应用于的文件类型
 */

/**
 * 获取文件目录信息
 * @param config
 * @returns FileCatalog
 */
function getCatalog({
  orderNum,
  catalogName,
  catalogType,
  bidFileType,
  uploadRequire,
  signRequire
}) {
  // 商务文件的目录中，开标一览表和分项报价表两种目录类型默认不展示，其余默认展示
  const hideInPageTypeList = [
    bidFileDirectoryTypeDict.MARK.value,
    bidFileDirectoryTypeDict.ITEMIZED.value
  ]
  const showInPage = !hideInPageTypeList.includes(catalogType)
  return {
    orderNum: orderNum,
    catalogName: catalogName,
    uploadRequire: uploadRequire,
    signRequire: signRequire,
    catalogType: catalogType,
    showInPage: showInPage,
    bidFileType: bidFileType
  }
}

export default {
  name: 'FileCatalogSetting',

  components: { BaseSectionTitle },

  model: {
    prop: 'list',
    event: 'change'
  },

  props: {
    list: {
      type: Array,
      default: () => []
    },

    readOnly: {
      type: Boolean,
      default: false
    },

    reviewModel: {
      type: Number,
      default: reviewModelDict.DEFAULT.value
    },

    // 目录所属的文件类型
    bidFileType: {
      type: Number,
      default: bidFileTypeDict.BUSINESS.value
    }
  },

  data() {
    return {
      form: {
        /**
         * @type FileCatalog[]
         */
        catalogList: []
      },
      rules: {
        catalogName: [
          requiredRule('请填写目录名称'),
          { validator: this.validateName, triggers: 'blur' }
        ],
        uploadRequire: requiredRule('请选择'),
        signRequire: requiredRule('请选择')
      }
    }
  },

  computed: {
    // 双信封模式
    isDouble() {
      return this.reviewModel === reviewModelDict.DOUBLE.value
    },

    sectionTitle() {
      let defaultTitle = '投标文件目录'
      // 双信封下，如果bidFileType为商务文件，则显示商务目录
      if (this.isDouble) {
        defaultTitle =
          bidFileTypeDict.translate(this.bidFileType)?.label + '文件目录'
      }
      if (!this.readOnly) {
        defaultTitle += '设置'
      }
      return defaultTitle
    }
  },

  watch: {
    list: {
      handler(val) {
        if (val?.length) {
          this.form.catalogList = cloneDeep(val).filter(item => {
            // 不显示分项报价目录和开标一览表目录
            const hideList = [
              bidFileDirectoryTypeDict.MARK.value,
              bidFileDirectoryTypeDict.ITEMIZED.value
            ]
            return !hideList.includes(item.catalogType)
          })
        }
      },
      deep: true,
      immediate: true
    },

    'form.catalogList': {
      handler(val) {
        if (val && !isEqual(val, this.list)) {
          const tempList = cloneDeep(val).map((item, index) => {
            return Object.assign({}, item, { orderNum: index + 1 })
          })

          this.$emit('change', tempList)
        }
      },
      deep: true
    }
  },

  created() {
    if (this.list.length === 0) this.initCatalog()
  },

  methods: {
    initCatalog() {
      let initCatalog = getCatalog({
        orderNum: 1,
        catalogName: bidFileDirectoryTypeDict.BID_TEXT.label,
        catalogType: bidFileDirectoryTypeDict.BID_TEXT.value,
        bidFileType: this.bidFileType,
        uploadRequire: true,
        signRequire: true
      })
      if (this.bidFileType === bidFileTypeDict.QUOTATION.value) {
        initCatalog = getCatalog({
          orderNum: 1,
          catalogName: bidFileDirectoryTypeDict.QUOTE.label,
          catalogType: bidFileDirectoryTypeDict.QUOTE.value,
          bidFileType: this.bidFileType,
          uploadRequire: true,
          signRequire: true
        })
      }

      // 默认有一个目录
      this.form.catalogList = [initCatalog]
    },

    addItem() {
      const catalogItem = getCatalog({
        orderNum: this.form.catalogList.length + 1,
        catalogType: bidFileDirectoryTypeDict.CUSTOMIZE.value,
        bidFileType: this.bidFileType
      })
      this.form.catalogList.push(catalogItem)
    },

    upItem(index) {
      moveInArray(this.form.catalogList, index, index - 1)
      this.form.catalogList.forEach((item, index) => {
        item.orderNum = index + 1
      })
    },

    showDownItem(index) {
      if (this.form.catalogList.length === 1) {
        return false
      }
      return index !== this.form.catalogList.length - 1
    },

    downItem(index) {
      moveInArray(this.form.catalogList, index, index + 1)
      this.form.catalogList.forEach((item, index) => {
        item.orderNum = index + 1
      })
    },

    showRemoveItem(index) {
      // 投标正文目录不可删除
      const catalog = this.form.catalogList[index]
      return catalog.catalogType !== bidFileDirectoryTypeDict.BID_TEXT.value
    },

    removeItem(index) {
      this.form.catalogList.splice(index, 1)
    },

    uploadRequireDisabled(index) {
      // // 投标目录正文不可修改是否必须上传
      // const catalog = this.form.catalogList[index]
      // return catalog.catalogType === bidFileDirectoryTypeDict.BID_TEXT.value
      // 根据双信封需求评审中关于评审办法设置交互的要求，目录正文可以修改是否需盖章和上传
      return false
    },

    signRequireDisabled(index) {
      // // 投标目录正文不可修改是否需盖章
      // const catalog = this.form.catalogList[index]
      // return catalog.catalogType === bidFileDirectoryTypeDict.BID_TEXT.value
      // 根据双信封需求评审中关于评审办法设置交互的要求，目录正文可以修改是否需盖章和上传
      return false
    },

    validateName(rule, value, callback) {
      const repeatObj = countRepeatNum(this.form.catalogList, 'catalogName')
      if (repeatObj[value].num > 1) {
        callback(new Error('当前目录名称已存在'))
        return
      }
      callback()
    },

    validateSettingForm() {
      return new Promise((resolve, reject) => {
        this.$refs.formRef.validate(valid => {
          if (!valid) {
            reject()
            this.$message.error('投标文件目录设置有误，请重新设置')
            return
          }
          resolve()
        })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.catalog-setting {
  background: $white-color;
  border-radius: 4px;
  padding: 20px;
}

.add-button {
  width: 100%;
  padding: 4px 0;
  border: 1px dashed $border-color;
  border-radius: 4px;
  text-align: center;
  margin-top: 16px;
  cursor: pointer;

  &:hover {
    border-color: $primary-color;
    color: $primary-color;
  }
}

::v-deep .el-form-item {
  margin-bottom: 26px;
  margin-top: 26px;
}
</style>
